import {useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router'
import {useTripDetails} from '@/hooks/useTrips'
import {useMyLocation} from '@/hooks/useMyLocation'
import {resolveWalkPlan} from '@/features/navigation/airportWalk'
import {tripFlightRecord} from '@/features/fromAirportArrival/tripFlightRecord'
import {clampStaticWalkStepIndex} from '@/features/navigation/data/yyzStaticWalkSteps'
import {useAnnounceArrival} from '@/features/fromAirportArrival/useAnnounceArrival'
import SilverMapBackground from '@/customComponents/fromAirportArrival/SilverMapBackground.jsx'
import WalkIllustration from '@/customComponents/fromAirportArrival/WalkIllustration.jsx'
import PlaceReferenceRow, {PlaceLightbox} from '@/customComponents/fromAirportArrival/PlaceReferenceRow.jsx'
import {carIcon, arrowRightSm} from '@img/index.js'

/**
 * THE WRITTEN DIRECTIONS — no map, no GPS, advanced by hand.
 *
 * ── ⚠️ THIS IS NOT A FALLBACK, AND BUILDING IT AS ONE WOULD BE THE MISTAKE ────────
 * Indoor GPS is genuinely unreliable, and a browser leans harder on wifi positioning than
 * a phone does — so a passenger inside a terminal with no usable fix is the normal case
 * rather than the edge. The mobile apps give this equal billing on the welcome screen and
 * so does this portal.
 *
 * ── ⚠️ THE STEPS ARE WALKED, NOT GENERATED ────────────────────────────────────────
 * They are the product owner's own on-site measured walk, timed in person, with
 * photographs of the real doorways. They are deliberately NOT derived from the routing
 * grids and are not expected to agree with them turn for turn. Do not "correct" them
 * against a solved route.
 *
 * ── ⚠️ THE SHAPE OF THE WALK IS THE MAIN THING ON SCREEN ──────────────────────────
 * It was a photograph, and a photograph answers only one question: "is this the door I am
 * standing at?" Useful once, at each end. What a passenger mid-walk needs is how many
 * corners are left and which way they bend, which is what the drawing shows — the whole
 * route as a ghost with the part already walked filled in. See `WalkIllustration`.
 *
 * The photographs are still here and still matter; they are the two thumbnails above,
 * which expand when someone actually wants to check a doorway.
 *
 * ── ⚠️ THE GUIDE IS THE SHEET'S VIEW, NOT THIS SCREEN'S ────────────────────────────
 * "How do I find my driver?" asks the PAGE to swap the sheet's view, so the way back is
 * the modal header's own back arrow. Owned here it was a second, weaker back control drawn
 * under a header that already has the space for one — and a modal over a modal would be
 * two close buttons and an ambiguous Escape key on a screen read while walking.
 */

/**
 * ⚠️ NOT A ROUTE. It is the welcome screen's modal content —
 * `/{tripId}/walk-to-pickup/steps` was removed once both walks moved into that sheet.
 * `tripId` comes from the caller — see the note on the component below.
 *
 * @param {Function} onOpenGuide swap the sheet to the airport pickup guide — the sheet
 *        owns that view so its header can carry the back arrow.
 */
/**
 * ⚠️ IT TAKES ITS `tripId` AS A PROP NOW (2026-09-23), and the `useParams` fallback is
 * only the old route's reading.
 *
 * This read the router alone, on the reasoning written here before: "the screen underneath
 * is `/{tripId}/welcome`". That route was deleted when the arrival guide became a modal, so
 * the screen underneath is whatever page the passenger happened to be on — the home page,
 * their rides, settings — and none of those carry a trip in the URL. `useParams` therefore
 * returned undefined, which is how "I've arrived" navigated to `/undefined/track-driver`
 * and why the trip fetched here was empty.
 *
 * `FromAirportArrived` knows the trip; it passes it in.
 */
/**
 * @param {Function} [onLeave] the walk is OVER — shut the sheet, and the arrival guide
 *   behind it. Navigating alone left both modals stacked over `/track-driver`: the
 *   passenger pressed "I've arrived", the URL changed underneath, and they were still
 *   looking at a walk they had just finished with a map they could not see.
 */
export default function WalkToPickupSteps({onOpenGuide, onLeave = null, tripId: tripIdProp = null}) {
    const {tripId: routeTripId} = useParams()
    const tripId = tripIdProp ?? routeTripId
    const navigate = useNavigate()
    /* ⚠️ `useTripDetails` RESOLVES TO `{trip, paymentMethods}`, NOT THE TRIP. Read as the
       trip itself, every field below is undefined and the screen renders its empty
       state on a perfectly good booking — silently, because nothing throws. */
    const {data} = useTripDetails(tripId)
    const trip = data?.trip || null

    /* Position is read to pick the terminal and nothing else. This screen works with the
       prompt declined, which is most of the reason it exists. */
    const me = useMyLocation()

    /* Which arrivals door these steps start from, and which photograph leads them. Looked
       up from the trip's flight number — the trip carries no flight record of its own. */
    const flight = tripFlightRecord(trip)

    const plan = useMemo(
        () => resolveWalkPlan(me.position, flight),
        [me.position, flight],
    )

    /* ⚠️ ONE PAST THE LAST STEP IS THE ARRIVED CARD, not an overflow. The index is clamped
       to `steps.length` rather than `length - 1` for exactly that reason. */
    const [index, setIndex] = useState(0)
    const [driverOpen, setDriverOpen] = useState(false)
    /* Which reference photograph is open in the lightbox — `'start'`, `'end'` or null. */
    const [openPlace, setOpenPlace] = useState(null)

    /* The arrival signal. It is a location ping carrying the booth's coordinate, because
       there is no arrival endpoint on this API — see `useAnnounceArrival`. The hook also
       owns the double-tap guard, which matters more here than on the welcome screen: this
       button sits under a thumb that has just pressed "Next step" four times. */
    const {announce} = useAnnounceArrival(trip)

    const steps = plan.steps
    const atEnd = index >= steps.length
    const step = atEnd ? null : steps[index]

    const go = (next) => setIndex(clampStaticWalkStepIndex(next, steps.length))

    const driver = trip?.driver
    const vehicle = driver?.vehicle
    const vehicleLine = [vehicle?.make, vehicle?.model].filter(Boolean).join(' ')

    return (
        /*
         * ── THE STAGE, AND A PANEL UNDER IT ─────────────────────────────────────────
         *
         * ⚠️ THE DRAWING GETS THE SPACE AND THE CONTROLS GET A FIXED FOOT. The instruction
         * and the two buttons must not move as the picture changes shape between steps — a
         * "Next step" button that shifts under a thumb, on a screen someone is reading
         * while walking, is the one thing this layout has to avoid. So the stage flexes and
         * the panel does not.
         *
         * ⚠️ THE SHEET IS ASKED TO DROP ITS GUTTERS, rather than this view cancelling
         * them with negative margins. `FromAirportArrived` passes `flushX`/`flushBody` for
         * both walks — the decorative map runs to the edges and the panel reaches the
         * bottom, so a reading gutter would frame a full-bleed stage in white and leave a
         * band under it. Negative margins would have to guess the sheet's padding at every
         * breakpoint and would silently be wrong the day it changed.
         *
         * The padding this view DOES want is put back below, on the content column and the
         * panel, where its own layout decides it.
         *
         * ⚠️ A FIXED HEIGHT, NOT A MINIMUM, AND THAT IS WHAT KEEPS THE WALK ON SCREEN.
         * With `min-h` the box grew to whatever its contents wanted, the sheet scrolled,
         * and the drawing ran off under the panel — so pressing "Next step" moved the
         * passenger marker somewhere the reader could not see. Pinned to the height it has,
         * `flex-1` on the stage becomes a real constraint and the picture is scaled to fit
         * instead of overflowing. The whole route is visible at every step, which is the
         * one promise this screen makes.
         */
        <div className="relative flex h-[70dvh] flex-col overflow-hidden max-md:h-auto max-md:min-h-[70dvh]">
            <SilverMapBackground/>

            {/* Everything sits above the decoration.

                ⚠️ `min-h-0` HERE TOO, not only on the stage inside it. A flex item refuses
                to shrink below its content by default, so without it this column keeps its
                natural height, the stage inside never gets squeezed, and the drawing is
                clipped by the root instead of scaled down to fit. Both levels have to give. */}
            <div className="relative flex min-h-0 flex-1 flex-col gap-4 px-4 pt-2 lg:px-8 max-md:gap-3">
                {/* ── THE TWO THINGS YOU CHECK BEFORE YOU SET OFF ───────────────────
                    "How do I find my driver?" and "what do the two ends look like?" are one
                    question each, asked once, at the start. Side by side they are a single
                    band across the top; stacked they were two, and the second one pushed the
                    drawing down the screen for the whole walk.

                    ⚠️ IT STACKS UNDER md, where two halves of a phone width leave the guide
                    row's subtitle wrapping and the thumbnails too small to recognise. */}
                {/* ⚠️ `justify-between`, SO THE THUMBS SIT ON THE RIGHT EDGE. The guide card
                    is sized to its text and the pair is capped at half the row, so neither
                    claims the leftover width — left alone it pooled at the end of the row
                    and the photographs floated in the middle with a hole beside them. The
                    free space belongs BETWEEN the two things, which is also what makes them
                    read as the two ends of one band rather than a pair pushed left. */}
                <div className="flex w-full items-start justify-between gap-3 max-md:flex-col">
                    {/* ⚠️ SIZED TO ITS OWN TEXT, NOT TO HALF THE ROW. As a `flex-1` half it
                        was a wide card holding two short lines, with the chevron marooned an
                        inch away from the label it belongs to — and the width it was taking
                        was width the thumbnails wanted, since a photograph of a doorway gets
                        more useful the bigger it is. Auto here, `flex-1` on the thumbs, so
                        the leftover space goes to the pictures. */}
                    <button
                        type="button"
                        onClick={onOpenGuide}
                        className="flex w-full shrink-0 cursor-pointer items-center gap-3 rounded-(--radius-xl) border border-(--border-color) bg-(--bg-white) p-3 text-left shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:opacity-90 md:w-auto"
                    >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-(--accent-green-light) text-(--text-primary) max-md:h-8 max-md:w-8">
                            <svg className="h-5 w-5 max-md:h-4 max-md:w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="9"/>
                                <path d="M9.5 9.5a2.6 2.6 0 1 1 3.2 2.5c-.5.2-.7.6-.7 1.1v.4"/>
                                <path d="M12 17h.01"/>
                            </svg>
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold text-(--text-primary) max-md:text-[13px]">How do I find my driver?</span>
                            <span className="block text-xs text-(--text-secondary)">See the airport pickup guide</span>
                        </span>
                        <img src={arrowRightSm} alt="" className="h-4 w-4 shrink-0 opacity-40"/>
                    </button>

                    {/* ⚠️ THE THUMBS STAY PUT WHEN ONE IS OPENED. They used to be replaced
                        in place by an expanded panel, which took the drawing's space and
                        cropped the photograph into a box built for a diagram. The full view
                        is its own dialog now — see `PlaceLightbox`. */}
                    {/* ⚠️ CAPPED AT HALF THE ROW. `flex-1` alone let the pair swallow every
                        pixel the guide card did not want, and on a wide sheet that is two
                        photographs the size of the walk itself — a reference thumbnail
                        growing past the diagram it is a footnote to. `flex-1` still lets it
                        SHRINK below half when the guide card's text is long. */}
                    <div className="w-full max-w-[50%] flex-1 max-md:max-w-full">
                        <PlaceReferenceRow plan={plan} onOpen={setOpenPlace}/>
                    </div>
                </div>

                {/* THE WALK ITSELF. `min-h-0` so the flex child may actually shrink —
                    without it a flex item refuses to go below its content height and the
                    drawing pushes the panel off the bottom of the sheet.

                    ⚠️ NO `max-h` ON THE DRAWING. A cap made sense while the box could grow;
                    now that the stage is exactly the space left over, capping it only wastes
                    that space — the SVG scales to fit whatever it is given, so letting it
                    have the lot is what makes a three-corner walk readable. */}
                <div className="flex min-h-0 flex-1 items-center justify-center py-2 max-md:min-h-[220px] max-md:flex-none max-md:h-[220px]">
                    <div className="h-full w-full max-w-[340px]">
                        <WalkIllustration steps={steps} index={index}/>
                    </div>
                </div>
            </div>

            {/* ── THE PANEL ──────────────────────────────────────────────────────────
                White and lifted off the decoration, so the words a passenger is following
                are never read against a map.

                ⚠️ TWO COLUMNS, AND THE SPLIT IS ABOUT HEIGHT. Stacked, the instruction, the
                buttons and the driver row are three bands that between them ate over half
                the sheet — and every pixel they took came off the drawing, which is the
                thing this screen is for. Side by side they cost the height of the taller
                one.

                ⚠️ IT STACKS AGAIN UNDER md. Two 50% columns on a phone are two columns of
                nothing; the instruction wraps to three lines and "Next step" ends up a
                thumb-width wide. */}
            <div className="relative mt-auto flex w-full flex-col items-center rounded-t-[28px] bg-(--bg-white) px-4 pb-6 pt-5 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] lg:px-8 max-md:rounded-t-[22px] max-md:pb-4 max-md:pt-3">
              {/* ⚠️ `justify-between`, AND NEITHER COLUMN GROWS. Both are fixed widths, so
                  the leftover space in the row has nowhere to be absorbed — `justify-between`
                  is what puts it BETWEEN them instead of letting it pool after the driver
                  card, which left the two boxes huddled on the left of an empty panel.

                  ⚠️ EXCEPT WITH NO DRIVER, WHEN IT CENTRES INSTEAD. `justify-between` on a
                  row holding ONE fixed-width child is just `flex-start`, so before a driver
                  is assigned the instruction and its buttons sat against the left edge of a
                  panel that was empty to their right — which reads as a layout that has come
                  apart rather than as a column waiting for its pair. */}
              <div className={`flex w-full max-w-[760px] items-start gap-4 max-md:flex-col max-md:gap-3 ${driver?.name ? 'justify-between' : 'justify-center'}`}>
                {/* WHAT TO DO, AND THE CONTROLS THAT ACT ON IT — one column, because the
                    buttons only make sense as "the way past THIS instruction". */}
                {/* ⚠️ A FIXED WIDTH, NOT `flex-1`. Growing to fill the row made the
                    instruction card a wide box holding a short sentence, and the Back /
                    Next pair under it two very long buttons — a control sized to the space
                    available rather than to the job.

                    ⚠️ 400px IS SET BY THE LONGEST HEADLINE, NOT PICKED. It is the floor for
                    keeping "Head straight to the pickup point" on one line, and only because
                    the headline dropped to `text-base` to get there. `flex-1` was coming out
                    at 464px, so an earlier cut to 440 was 24px of nothing and looked
                    identical on screen.

                    ⚠️ `min-w-0` STAYS. A flex item will not shrink below its content
                    without it, so the headline's `truncate` would have nothing to truncate
                    against and would push this column past its own width instead. */}
                <div className="flex w-full min-w-0 flex-col gap-3 md:w-[400px] md:shrink-0">
                {/* ── THE INSTRUCTION ────────────────────────────────────────────────
                    ⚠️ THE ARROW IS ROTATED BY THE RECORDED ANGLE, not picked from a set of
                    icons. The walk data carries degrees (0 straight, negative left), so one
                    arrow turned by that number can never disagree with the words beside it
                    — which a hand-matched icon eventually does. */}
                <div className="flex w-full items-center gap-4 rounded-(--radius-xl) bg-(--bg-white) p-4 shadow-[0_8px_24px_rgba(0,0,0,0.08)] max-md:gap-3 max-md:p-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-(--accent-green-light) text-(--text-primary) max-md:h-9 max-md:w-9">
                        {atEnd ? (
                            /* A flag, not a tick: the walk is over, which is not the same
                               claim as "your driver has you". */
                            <svg className="h-5 w-5 max-md:h-4 max-md:w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M5 21V4l9 3.5L5 11"/>
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5 max-md:h-4 max-md:w-4"
                                style={{transform: `rotate(${step.angle}deg)`}}
                                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                            >
                                <path d="M12 20V5M6 11l6-6 6 6"/>
                            </svg>
                        )}
                    </span>
                    <span className="min-w-0 flex-1">
                        {/* ⚠️ A MANEUVER IS ONE LINE; THE ARRIVAL SENTENCE IS NOT.
                            "Head straight to the pickup point" wrapping onto two lines made
                            the card grow and the drawing above it shrink at that step only,
                            so the layout twitched as the passenger advanced. Every step
                            headline is short enough to hold one line in this column — see
                            the widths below — and `truncate` guarantees it for any future
                            one that is not.

                            ⚠️ THE ARRIVED CARD IS EXEMPT, DELIBERATELY. Its headline is a
                            sentence rather than a maneuver ("Look to your right — you'll see
                            airport officials at the pickup point") and truncating THAT
                            removes the only instruction on the screen. It can afford the
                            room: the arrival card carries no distance line and no step
                            counter, so it is already the shortest of them. */}
                        <span className={`block font-bold text-(--text-primary) ${
                            atEnd ? 'text-base max-md:text-sm' : 'truncate text-base max-md:text-sm'
                        }`} title={atEnd ? undefined : step.headline}>
                            {atEnd ? plan.arrivalHeadline : step.headline}
                        </span>
                        {/* ⚠️ "0 m" IS NOT SHOWN. The generic fallback plan carries a
                            distance of zero because it is a placeholder for a walk nobody
                            has measured, and printing it states a measurement that was
                            never taken. Mobile shows it; the doc flags it as worth fixing. */}
                        {!atEnd && step.distanceMeters > 0 && (
                            <span className="block text-sm text-(--text-secondary) max-md:text-xs">{step.distanceMeters} m</span>
                        )}
                    </span>
                    {/* ⚠️ THE COUNTER IS HIDDEN ON A ONE-STEP WALK. "1 of 1" is not
                        progress, it is a label pretending to be one. */}
                    {!atEnd && steps.length > 1 && (
                        <span className="shrink-0 text-sm text-(--text-secondary) max-md:text-xs">{index + 1} of {steps.length}</span>
                    )}
                </div>

                {/* ⚠️ NO PROGRESS DOTS. They said the same thing as the "3 of 3" already on
                    the card beside the instruction, and the drawing above says it a third
                    time and better — the walked part of the line IS the progress. Three
                    indicators of one fact, each costing height the drawing wanted. */}

                <div className="flex w-full items-center gap-3">
                    <button
                        type="button"
                        onClick={() => go(index - 1)}
                        disabled={index === 0}
                        className="min-h-13 flex-1 cursor-pointer rounded-full border-0 bg-(--bg-grey) px-4 font-semibold max-md:min-h-10 max-md:text-sm text-(--text-primary) disabled:cursor-default disabled:opacity-50"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        /* ⚠️ THE LAST PRESS TELLS THE DRIVER, and it is the only thing that
                           does: the arrival is a location ping carrying the booth's
                           coordinate, which their app measures against its own copy of the
                           same booths. See `useAnnounceArrival`. It is fire-and-forget — the
                           endpoint reports nothing back — so navigation does not wait on it.

                           ⚠️ IT PUSHES, SO BACK RETURNS TO THE WELCOME SCREEN — the same
                           as every other page in the portal. It used to replace the entry,
                           which left a passenger who had reached tracking from here unable
                           to get back. The steps are a view inside that screen's sheet, not
                           a route, so back lands on the welcome screen with no sheet open
                           and there is nothing to walk out of twice. */
                        onClick={() => {
                            if (!atEnd) { go(index + 1); return }
                            announce()
                            navigate(`/${tripId}/track-driver`)
                            /* ⚠️ AFTER the navigation, not instead of it — see `onLeave`. */
                            onLeave?.()
                        }}
                        className="min-h-13 flex-1 cursor-pointer rounded-full border-0 bg-(--text-primary) px-4 font-semibold max-md:min-h-10 max-md:text-sm text-(--bg-white)"
                    >
                        {atEnd ? 'I’ve arrived' : 'Next step'}
                    </button>
                </div>
                </div>

                {/* ── DRIVER DETAILS, FOLDED AWAY ────────────────────────────────────
                    Collapsed by default: while someone is walking, the car is the answer to
                    a question they have not reached yet, and open it would push the
                    instruction off a phone screen.

                    ⚠️ IT IS THE SECOND COLUMN, NOT A THIRD BAND. With no driver assigned yet
                    the column simply is not there, and the row centres its remaining child
                    rather than leaving a gap where a car should be — see the `justify-*`
                    switch on the row above.

                    ⚠️ ITS OWN HEIGHT. It was `self-stretch`, which matched the left column —
                    a collapsed row three lines tall, drawing a tall empty box under one line
                    of text. A disclosure should be exactly as big as what it is currently
                    showing, so `self-start` lets it hug its content and grow only when it is
                    opened.

                    ⚠️ AND A FIXED WIDTH RATHER THAN AN EVEN HALF. It holds two short lines
                    and a chevron; the instruction beside it holds a sentence that has to fit
                    on ONE line. Splitting the row down the middle spent that width on empty
                    space here and forced "Head straight to the pickup point" to wrap over
                    there, which grew the card and shrank the drawing at that step only. This
                    column takes what it needs and the instruction takes the rest. */}
                {driver?.name && (
                    <div className="w-full self-start rounded-(--radius-xl) border border-(--border-color) bg-(--bg-white) md:w-[280px] md:shrink-0">
                        <button
                            type="button"
                            onClick={() => setDriverOpen((open) => !open)}
                            aria-expanded={driverOpen}
                            className="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent p-3 text-left max-md:p-2.5"
                        >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-(--bg-grey) max-md:h-8 max-md:w-8">
                                <img src={carIcon} alt="" className="h-4 w-auto"/>
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-bold text-(--text-primary) max-md:text-[13px]">Driver details</span>
                                {/* ⚠️ NOT TRUNCATED — see the name below. A car a passenger
                                    is about to look for in a car park is worth a second line
                                    of this column. */}
                                <span className="block text-xs text-(--text-secondary)">
                                    {vehicleLine || driver.name}
                                </span>
                            </span>
                            {/* ⚠️ ITS OWN CHEVRON, NOT THE SHARED ARROW TURNED SIDEWAYS. A
                                disclosure points DOWN when closed and up when open; rotating
                                a right-pointing arrow to fake that is a control whose
                                direction depends on which asset someone swaps in later. */}
                            <svg
                                className={`h-4 w-4 shrink-0 text-(--text-secondary) transition-transform ${driverOpen ? 'rotate-180' : ''}`}
                                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                            >
                                <path d="m6 9 6 6 6-6"/>
                            </svg>
                        </button>

                        {driverOpen && (
                            <div className="flex flex-wrap items-center gap-3 px-3 pb-3">
                                {driver.photo && (
                                    <img src={driver.photo} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover max-md:h-9 max-md:w-9"/>
                                )}
                                <span className="min-w-0 flex-1">
                                    {/* ⚠️ NEVER TRUNCATED. "Hamza Ja…" is not a name a
                                        passenger can match against the person holding a
                                        board with theirs on it, and this panel exists for
                                        exactly that moment. It is opened deliberately and
                                        holds four short fields, so it can afford the line. */}
                                    <span className="block font-bold text-(--text-primary)">{driver.name}</span>
                                    {vehicle?.color && <span className="block text-sm text-(--text-secondary)">{vehicle.color}</span>}
                                </span>
                                {vehicle?.plate_no && (
                                    <span className="shrink-0 rounded-full bg-(--bg-grey) px-4 py-2 text-sm font-medium text-(--text-primary)">
                                        {vehicle.plate_no}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
              </div>
            </div>

            {/* ⚠️ RENDERED WHATEVER IS OPEN, so the sheet keeps it mounted through its
                close animation instead of the photograph vanishing a frame before the
                dialog does. `plan` always has both entries; `openPlace` only chooses. */}
            <PlaceLightbox
                open={!!openPlace}
                onClose={() => setOpenPlace(null)}
                photo={openPlace === 'start' ? plan.startPhoto : plan.endPhoto}
                title={openPlace === 'start' ? plan.startLabel : plan.endLabel}
                description={openPlace === 'start' ? plan.startDescription : plan.endDescription}
            />
        </div>
    )
}

export {WalkToPickupSteps}
